import type { z } from 'zod';

import { env } from '../../../config/env';
import { newRequestId } from '../utils/request-id';

/**
 * The single, thin network seam between US-Shop and the BFF (E-SF02).
 *
 * ── WHY US-Shop DOES NOT USE @fluxora/api-client ────────────────────────────
 * The CRM's `@fluxora/api-client` injects `Authorization` + `x-tenant-id` on
 * every call. The PUBLIC storefront must send NEITHER: it holds no service token,
 * and tenant is derived SERVER-SIDE by the BFF from the request Host — a
 * browser-supplied tenant id would be a cross-tenant read waiting to happen
 * (ADR-0002 / E-SF01 AC1b). So the storefront talks to same-origin `/bff/*`
 * paths (Vite-proxied to :3031 in dev) with a plain, credential-free fetch. This
 * one wrapper is the only place `fetch` is called, keeping that rule enforceable.
 *
 * ── FAIL LOUD ───────────────────────────────────────────────────────────────
 * A non-2xx becomes a `BffError` carrying the status (so a 404 can be told apart
 * from an outage), and a response whose shape does not match the contract schema
 * is also an error — never a silently-degraded empty result.
 */
export class BffError extends Error {
  public override readonly name = 'BffError';
  public readonly status: number | undefined;
  /** The parsed RFC7807 problem-details body, when the BFF returned one. */
  public readonly problem: ProblemBody | undefined;
  /** Correlation id from the problem body — pairs a UI failure to the BFF logs. */
  public readonly correlationId: string | undefined;

  public constructor(
    message: string,
    status?: number,
    detail?: { problem?: ProblemBody; correlationId?: string },
  ) {
    super(message);
    this.status = status;
    this.problem = detail?.problem;
    this.correlationId = detail?.correlationId;
  }
}

/** The shape the BFF returns on a failure (RFC7807 problem+json). All fields optional — we only read. */
interface ProblemBody {
  readonly type?: string;
  readonly title?: string;
  readonly status?: number;
  readonly detail?: string;
  readonly correlationId?: string;
}

/**
 * Turn a non-2xx response into a rich, debuggable `BffError`.
 *
 * The BFF sanitizes 5xx bodies (internals never cross the boundary), so a 500's
 * real reason is NOT in the JSON `detail` — in a NON-deployed BFF it rides the
 * dev-only `x-bff-error-detail` header instead. We read both, fold them into the
 * thrown message, and `console.error` the whole failure so a developer sees WHY
 * a call failed straight in the DevTools console — no Network-tab spelunking, no
 * grepping BFF logs by correlation id.
 */
async function bffErrorFromResponse(path: string, res: Response): Promise<BffError> {
  let problem: ProblemBody | undefined;
  const rawBody =
    typeof res.text === 'function' ? await res.text().catch(() => '') : '';
  if (rawBody !== '') {
    try {
      problem = JSON.parse(rawBody) as ProblemBody;
    } catch {
      problem = undefined;
    }
  }

  // Dev-only: the un-sanitized internal reason (URL-encoded on the header).
  const devHeader = res.headers?.get('x-bff-error-detail') ?? null;
  const devDetail = devHeader === null ? undefined : safeDecode(devHeader);

  const correlationId = problem?.correlationId;
  const reason = devDetail ?? problem?.detail ?? problem?.title;
  const message =
    `Storefront service returned ${res.status} for ${path}` +
    (reason !== undefined && reason !== '' ? ` — ${reason}` : '') +
    (correlationId !== undefined ? ` (correlationId ${correlationId})` : '');

  // Loud, structured console trace — this is the "so it's easy to debug" ask.
  // eslint-disable-next-line no-console
  console.error('[bff] request failed', {
    path,
    status: res.status,
    detail: problem?.detail,
    devDetail,
    correlationId,
    problem,
  });

  return new BffError(message, res.status, { problem, correlationId });
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** True when the failure was an upstream 404 (resource genuinely absent). */
export function isNotFound(error: unknown): boolean {
  return error instanceof BffError && error.status === 404;
}

/**
 * True when the BFF refused for want of a funnel session (401).
 *
 * ⚠️ THE RIGHT ANSWER TO THIS IS "GO BACK TO YOUR ADDRESS", NOT "MINT A SESSION".
 * Only the meter sub-flows establish a session (`useEnsureSessionQuery`, called
 * from the landing method chooser / ZIP / ESI ID sub-flows — SHOP-6 removed the
 * `AddressPage` this note used to name, and it is no longer routed), so a visitor
 * who deep-links straight to a later step has no cookie and every session-required
 * POST 401s. Quietly minting a session on the credit step would make that deep link
 * WORK — turning a step that is supposed to follow serviceability into an anonymous,
 * enumerable entry point. So a 401 is surfaced as the existing "start with your
 * address" affordance, which is both truthful and the only route that legitimately
 * creates a session.
 */
export function isSessionExpired(error: unknown): boolean {
  return error instanceof BffError && error.status === 401;
}

export interface BffGetOptions {
  /**
   * Send an `x-captcha-token` on this GET — for a captcha-gated GET like
   * `/bff/address/suggest`. Off by default (plain public GETs send no token). A
   * real Turnstile token when the widget is wired; otherwise the dev stub (gated
   * by `VITE_DEV_CAPTCHA_STUB`, set only in .env.development). ⚠️ Per-request
   * single-use captcha on an autocomplete GET is impractical in prod — the
   * effective server controls are the session requirement + rate-limit; in dev
   * the stub passes the null verifier. See bff-client / STUBS.md.
   */
  readonly withCaptcha?: boolean;
  /** An explicit captcha token to send (when the widget is wired). */
  readonly captchaToken?: string;
}

export async function bffGet<T>(
  path: string,
  schema: z.ZodType<T>,
  options: BffGetOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.withCaptcha === true) {
    const captchaToken =
      options.captchaToken !== undefined && options.captchaToken !== ''
        ? options.captchaToken
        : env.devCaptchaStub
          ? 'dev-captcha-stub'
          : undefined;
    if (captchaToken !== undefined) headers['x-captcha-token'] = captchaToken;
  }

  let res: Response;
  try {
    res = await fetch(path, {
      method: 'GET',
      headers,
      credentials: 'same-origin',
    });
  } catch (err) {
    throw new BffError(
      `Storefront service unreachable at ${path}: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  if (!res.ok) {
    throw await bffErrorFromResponse(path, res);
  }

  let json: unknown;
  try {
    json = await res.json();
  } catch (err) {
    throw new BffError(
      `Storefront service returned invalid JSON for ${path}: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new BffError(`Storefront service returned an unexpected shape for ${path}`);
  }
  return parsed.data;
}

/** A binary document fetched from the BFF, plus the name the server gave it. */
export interface BffBlob {
  readonly blob: Blob;
  /** From `content-disposition`, or the caller's fallback. Never a path. */
  readonly filename: string;
}

/**
 * `content-disposition: …; filename="…"` → the filename.
 *
 * Returns null on anything unexpected so the caller falls back rather than naming
 * a file after a malformed header. Any path component is stripped: the server
 * names the file, but it names a FILE.
 */
export function filenameFromDisposition(header: string | null): string | null {
  if (header === null) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/iu.exec(header);
  const raw = match?.[1]?.trim();
  if (raw === undefined || raw.length === 0) return null;
  return raw.replace(/^.*[\\/]/u, '');
}

export interface BffGetBlobOptions {
  /** MIME type to ask for. Defaults to `application/pdf`. */
  readonly accept?: string;
  /** Used when the response carries no usable `content-disposition` filename. */
  readonly fallbackFilename: string;
  /** Aborts the in-flight request (component unmount, a superseding retry). */
  readonly signal?: AbortSignal;
}

/**
 * A credential-free GET that returns BINARY BYTES rather than JSON (E-SF18).
 *
 * ── WHY THIS BELONGS HERE AND NOT IN A COMPONENT ───────────────────────────
 * `bff-client.ts` is the ONE place this app calls `fetch`, and that rule is what
 * makes "the storefront sends no Authorization and no x-tenant-id" enforceable by
 * inspection rather than by memory. A PDF is still an HTTP call to the BFF, so it
 * goes through the same seam and inherits the same posture, the same `BffError`
 * (status + problem body + correlation id), and the same loud console trace.
 *
 * ── WHY NOT `<iframe src="/bff/…">` ────────────────────────────────────────
 * Because the browser would own the request, and therefore own the failure. The
 * app could not see a 503, could not tell a slow render from a dead endpoint, and
 * could not offer a retry — which is precisely the trap `SignedAgreementDownload`
 * documented when it stopped being an `<a download>`. Fetching the bytes ourselves
 * buys the pending state, the typed error and the retry; the object URL then hands
 * the browser's own PDF viewer exactly what it would have fetched anyway.
 */
export async function bffGetBlob(
  path: string,
  options: BffGetBlobOptions,
): Promise<BffBlob> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: 'GET',
      headers: { accept: options.accept ?? 'application/pdf' },
      // Same-origin, matching every other BFF call from this app — the funnel
      // session cookie is what authorises the read.
      credentials: 'same-origin',
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (err) {
    throw new BffError(
      `Storefront service unreachable at ${path}: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  if (!res.ok) {
    throw await bffErrorFromResponse(path, res);
  }

  const blob = await res.blob();
  // A zero-byte 200 is a failure wearing a success code — never hand a viewer an
  // empty document and let it render a blank page as if that were the contract.
  if (blob.size === 0) {
    throw new BffError(`Storefront service returned an empty document for ${path}`, res.status);
  }

  return {
    blob,
    filename:
      filenameFromDisposition(res.headers?.get('content-disposition') ?? null) ??
      options.fallbackFilename,
  };
}

export interface BffPostOptions {
  /**
   * ⚠️ CAPTCHA HOOK (E-SF03). A Turnstile token for a sensitive step. Undefined
   * in dev (the BFF's null verifier passes under the free allowance); the real
   * widget is deferred (STUBS.md). When present it rides as `x-captcha-token`.
   */
  readonly captchaToken?: string;
  /**
   * ⚠️ THE KEY FOR A LOGICAL OPERATION, when the caller owns one.
   *
   * Omitted, this function mints a fresh key per REQUEST — correct for a one-shot
   * POST, and wrong for anything a customer can trigger twice. QA 2026-08-05
   * (QA-08-05-05) double-clicked `Sign and enroll` and got two requests with two
   * DIFFERENT keys, so the BFF's idempotency layer was never consulted for the
   * second: two distinct keys are, by construction, two operations. The only thing
   * that prevented a duplicate contract was Quotation's state machine — a
   * server-side invariant this client does not own.
   *
   * A caller that can be re-submitted must therefore mint its key ONCE, when the
   * operation begins, and pass it here for every retry/replay of that same
   * operation. `sign-operation.service.ts` is the reference implementation, and it
   * also explains why the request BODY has to be stabilised alongside the key
   * (same key + different body is a 409, not a replay).
   */
  readonly idempotencyKey?: string;
}

/**
 * A credential-free POST to the BFF (E-SF03). Same posture as `bffGet`: NO
 * Authorization, NO x-tenant-id — the BFF derives tenant from Host. Adds the two
 * things a public POST needs (E-SF01): an `Idempotency-Key` on every request and
 * a forward `x-correlation-id`. The BFF session cookie rides via `same-origin`.
 */
export async function bffPost<TReq, TRes>(
  path: string,
  body: TReq,
  schema: z.ZodType<TRes>,
  options: BffPostOptions = {},
): Promise<TRes> {
  const headers: Record<string, string> = {
    accept: 'application/json',
    'content-type': 'application/json',
    // Operation-scoped when the caller owns one; otherwise per-request. See
    // `BffPostOptions.idempotencyKey`.
    'idempotency-key':
      options.idempotencyKey !== undefined && options.idempotencyKey !== ''
        ? options.idempotencyKey
        : newRequestId(),
    // The correlation id is ALWAYS per-request, even inside one operation: it pairs
    // THIS network attempt to THIS line in the BFF log, and collapsing two attempts
    // onto one id would make a replayed request indistinguishable from the original
    // in the trace.
    'x-correlation-id': newRequestId(),
  };
  // A real Turnstile token (when the widget is wired) rides as `x-captcha-token`.
  // In dev, the CAPTCHA-unconditional routes (credit/sign) 403 on a MISSING token
  // BEFORE the BFF null-verifier runs, so with no widget yet we send a dev-only
  // placeholder (gated by VITE_DEV_CAPTCHA_STUB, set only in .env.development).
  // Fails closed in prod: the real siteverify rejects a stub token.
  const captchaToken =
    options.captchaToken !== undefined && options.captchaToken !== ''
      ? options.captchaToken
      : env.devCaptchaStub
        ? 'dev-captcha-stub'
        : undefined;
  if (captchaToken !== undefined) {
    headers['x-captcha-token'] = captchaToken;
  }

  let res: Response;
  try {
    res = await fetch(path, {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new BffError(
      `Storefront service unreachable at ${path}: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  if (!res.ok) {
    throw await bffErrorFromResponse(path, res);
  }

  let json: unknown;
  try {
    json = await res.json();
  } catch (err) {
    throw new BffError(
      `Storefront service returned invalid JSON for ${path}: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new BffError(`Storefront service returned an unexpected shape for ${path}`);
  }
  return parsed.data;
}

/**
 * The RFC7807 `type` the BFF refused with, or `undefined` when there is none.
 *
 * Sits here with `isNotFound` / `isSessionExpired` / `isNotImplemented` because
 * this is the one module that knows what a `BffError` is. It exists because some
 * refusals cannot be told apart by STATUS — `esiid-already-enrolled` and
 * `esiid-not-active` are both 409s with different remedies — so the branch has to
 * read the stable `type` URN. Returning the bare string keeps the classification
 * itself in `services/`, pure and free of any API-layer type.
 */
export function problemTypeOf(error: unknown): string | undefined {
  return error instanceof BffError ? error.problem?.type : undefined;
}

/**
 * The RFC 7807 `status` and `detail` the BFF refused with, for the ONE service that
 * judges whether the detail may be shown (`services/bff-detail.service.ts`).
 *
 * Sits beside `problemTypeOf` for the same reason: this module is the only one that
 * knows what a `BffError` is. It returns plain fields rather than the error, so the
 * decision stays pure and in `services/`.
 */
export function problemFieldsOf(error: unknown): {
  readonly status: number | undefined;
  readonly detail: string | undefined;
} {
  if (!(error instanceof BffError)) return { status: undefined, detail: undefined };
  return { status: error.status, detail: error.problem?.detail };
}

/** True when the failure was an upstream 501 (endpoint deliberately not built yet). */
export function isNotImplemented(error: unknown): boolean {
  return error instanceof BffError && error.status === 501;
}
